/**
 * JavaScript version of the Auth service API.
 */
let refreshSessionPromise = null

export default class AuthCli {

  //
  // Path constants:
  //
  static get AUTH_PATH() { return 'auth' }
  static get REALMS_PATH() { return 'realms' }
  static get REALM_PATH() { return 'realm' }
  static get INVITATION_PATH() { return 'invitation' }
  static get USER_PATH() { return 'user' }
  static get SESSION_PATH() { return 'session' }
  static get PASSWORDS_PATH() { return 'passwords' }
  static get BILLABLE_USERS_PATH() { return 'billableusers' }
  static get PERMISSIONS_PATH() { return 'permissions' }
  static get ROLES_PATH() { return "roles" }
  static get USER_ID_QUERY() { return "userId" }
  static get REALM_QUERY() { return "realm" }
  static get ROLE_QUERY() { return "role" }
  static get GRANTED_PATH() { return "granted" }
  static get PERMISSION_CODE_QUERY() { return "permissionCode" }
  static get DENIED_PATH() { return "denied" }
  static get ALL_USERS_PATH() { return "allusers" }
  static get PAGE_SIZE_QUERY() { return 'pagesize' }
  static get PAGE_QUERY() { return 'page' }
  static get CULTURE_QUERY() { return "culture" }
  static get PASSWORD_RECOVERY_PATH() { return 'passwordrecovery' }
  static get PASSWORD_EXPIRATION_CHECK_PATH() { return 'passwordexpirationcheck' }
  static get USER_FEATURES_PATH() { return 'userfeatures' }
  static get AUTH_MODE_HEADER_NAME() { return 'X-Auth-Mode' }
  static get AUTH_MODE_COOKIE_VALUE() { return 'cookie' }
  static get CSRF_COOKIE_NAME() { return 'pm_csrf' }
  static get CSRF_HEADER_NAME() { return 'X-CSRF-TOKEN' }
  static get SESSION_EXPIRED_EVENT_NAME() { return 'postamail:session-expired' }
  static get SESSION_EXPIRED_RESPONSE() { return '[ERR_AUTH_SESSION_EXPIRED] Session expired' }
  static get REFRESH_SESSION_LOCK_NAME() { return 'postamail:refresh-session' }

  // Other constants:
  static get MFA_OPTION_EMAIL() { return 'Email' }

  // URL prefix for debugging environment vs behind reverse proxy environment.
  static get devPrefix() { return (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') ? 'http://localhost:6005/' : window.location.protocol + '//' + window.location.host + '/api/' }

  /**
   * Provides the information about all currently existing invitations that
   * are still valid (not expired) and still not confirmed.
   * @returns InvitationInfo: { userEmail, realm, role, invitationTime, invitationExpiration }
   */
  static async GetAllInvitations() {
    var verb = 'GET'
    var path = this.AUTH_PATH + '/' + this.INVITATION_PATH
    var body = null

    return this.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the information about all currently existing invitations that
   * are still valid (not expired) and still not confirmed, for the realm in context.
   * This is to be called by working group administrators.
   * @returns InvitationInfo: { userEmail, realm, role, invitationTime, invitationExpiration }
   */
  static async GetAllInvitationsOfRealm() {
    var verb = 'GET'
    var path = this.AUTH_PATH + '/' + this.INVITATION_PATH+ '/' + this.REALM_PATH
    var body = null

    return this.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the information about one of the currently valid invitations given the invitation token.
   * If the invitation has already been used or expired no information is provided.
   * @param {string} token Invitation token as sent to the user in the invitation e-mail
   * @returns 
   */
  static async GetInvitation(token) {
    var verb = 'GET'
    var path = this.AUTH_PATH + '/' + this.INVITATION_PATH + '/' + token
    var body = null

    return this.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the list of all realms currently in the system.
   * Used to select any realm for invitations.
   * Note that this list includes billables but also other types of realms.
   * @returns {string[]} Realm identifiers, as shown to the user.
   */
  static async GetAllRealms() {
    var verb = 'GET'
    var path = this.AUTH_PATH + '/' + this.REALMS_PATH
    var body = null

    return this.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Request to send an invitation to work on the system to a new user.
   * The future user will receive an invitation e-mail message with a link
   * to confirm and create the credentials to access the realm where is
   * being invited.
   * @param {Invitation} invitation { string userEmail, string realm, string role, string culture, string mfaRequired }
   * @returns Void
   */
  static async Invite(invitation) {
    var verb = 'POST'
    var path = this.AUTH_PATH + '/' + this.INVITATION_PATH
    var body = invitation
    return this.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Removes an invitation pending to be accepted.
   * @param {Invitation} invitation information of the invitation requested to be invoked
   * @returns Error message if not possible to revoke
   */
  static async RevokeInvitation(invitation) {
    var verb = 'DELETE'
    var path =  this.AUTH_PATH + '/' + this.INVITATION_PATH
    var body = invitation

    return this.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * A previously invited user is calling to confirm the registration providing the
   * initial credentials.
   * @param {SignUpCredentials} credentials { string userEmail, string userPassword, string invitationToken }
   * @returns String: Error messages in case of invalid credentials. Null on sign up success
   */
  static async SignUp(credentials) {
    var verb = 'POST'
    var path = this.AUTH_PATH + '/' + this.USER_PATH
    var body = credentials

    return this.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Unregisters the currently logged in user from the system.
   * This user will never be able to log in.
   * @param {string} password Current user's password for double-check
   * @returns Error message in case of invalid password. Null on success
   */
  static async DropOut(password) {
    var verb = 'PUT'
    var path = this.AUTH_PATH + '/' + this.USER_PATH
    var body = password

    return this.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides information about the user currently logged in.
   * To be used by other services to know about the user being accessing them.
   * @returns {SessionInfo} Information about the user. Null if no session is open { string userId, string userEmail, string realm, string role, string[] permissionsIn, string[] permissionsOut }
   */
  static async GetSessionInfo() {
    var verb = 'GET'
    var path = this.AUTH_PATH + '/' + this.SESSION_PATH
    var body = null
    const currentPath = window.location.pathname
    const loginNotNeeded = this.PATHS_NOT_NEED_LOGIN.includes(currentPath) || currentPath === '/docs/api' || currentPath.startsWith('/docs/api/')

    return this.Fetch(this.devPrefix + path, verb, body).then(async session => {
      if (session) return this.DiscardForeignSession(session, loginNotNeeded)
      if (loginNotNeeded) return null
      await this.RefreshSession()
      return this.Fetch(this.devPrefix + path, verb, body).then(session => this.DiscardForeignSession(session, loginNotNeeded))
    })
  }

  /**
   * Session cookies are shared with the BackOffice, so a session opened there is not valid for this application.
   * Such a session is treated as missing: the user is asked to log in again (unless the current page does not need login).
   * @param {SessionInfo|string|null} session Session information as returned by the Auth service.
   * @param {boolean} loginNotNeeded Whether the current page can be used without login.
   * @returns {SessionInfo|string|null} The same session, or null if it belongs to the BackOffice.
   */
  static DiscardForeignSession(session, loginNotNeeded) {
    if (!session || typeof session !== 'object' || this.IsSessionOfThisApp(session)) return session
    if (!loginNotNeeded) this.NotifySessionExpired(window.location.pathname.slice(1) + window.location.search)
    return null
  }

  /**
   * Whether the session is not bound to a BackOffice realm (system or factory realms).
   * Sessions still pending to select a realm belong to the FrontOffice.
   * @param {SessionInfo} session Session information.
   * @returns {boolean} True if the session belongs to the FrontOffice.
   */
  static IsSessionOfThisApp(session) {
    const realm = session.realm || ''
    return !realm.startsWith('system') && !realm.startsWith('FACTORY__')
  }

  /**
   * Validates the callback to navigate to after login, accepting only relative paths within this application.
   * @param {string} callback Callback received in the query string.
   * @param {string} defaultPath Path to use when the callback is missing or not valid.
   * @returns {string} A safe relative path to navigate to.
   */
  static SanitizeCallback(callback, defaultPath) {
    if (typeof callback !== 'string') return defaultPath
    const path = callback.split(/[?#]/)[0]
    if (!/^[A-Za-z0-9][A-Za-z0-9/_.-]*$/.test(path) || path.includes('//') || path.includes('..')) return defaultPath
    if (path.toLowerCase() === 'backoffice' || path.toLowerCase().startsWith('backoffice/')) return defaultPath
    return callback
  }

  /**
   * Initiates a new session for a user to work in the system within a specified realm.
   * @param {LoginCredentials} credentials { string userEmail, string userPassword, string realm, string delegateEmail }
   * @returns {Session} Session payload returned by backend in cookie mode (tokens are not expected in body).
   */
  static async LogIn(credentials) {
    var verb = 'POST'
    var path = this.AUTH_PATH + '/' + this.SESSION_PATH
    var body = credentials

    return fetch(this.devPrefix + path, this.GetRequestOptions(verb, body))
      .then(async response => {
        if (response.status === 204) return null
        if (response.status === 401) return response.text()
        if (response.status === 200) return response.json()
        return response.text()
      })
  }
  
  /**
   * List of path that do not require login.
   * These paths are used to avoid redirecting to login when the user is not logged in.
   */
  static get PATHS_NOT_NEED_LOGIN() {
    return [
      '/login',
      '/replacepassword',
      '/signup',
      '/recoverpassword',
      '/docs/api',
    ]
  }
  
  /**
   * Retrieves the value of a specified cookie by name.
   * @param {string} name The name of the cookie to retrieve.
   * @returns {string|null} The value of the cookie, or null if not found.
   */
  static GetCookie(name) {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'))
    return match ? decodeURIComponent(match[1]) : null
  }
  
  /**
   * Determines if an HTTP verb is state-changing (i.e., modifies server-side state).
   * @param {string} verb The HTTP verb to check.
   * @returns {boolean} True if the verb is state-changing, otherwise false.
   */
  static IsStateChangingVerb(verb) {
    return verb === 'POST' || verb === 'PUT' || verb === 'PATCH' || verb === 'DELETE'
  }
  
  /**
   * Notifies the application that the user's session has expired.
   * @param {function} callback The callback to execute when the session expiration is handled.
   */
  static NotifySessionExpired(callback) {
    if (window.postamailSessionExpiredNotified) return
    window.postamailSessionExpiredNotified = true
    window.dispatchEvent(new CustomEvent(this.SESSION_EXPIRED_EVENT_NAME, { detail: { callback: callback } }))
  }

  /**
   * Requests the Auth service to renew the session identifier in order to keep a previous login session alive.
   * Session renewal is managed by secure cookies emitted by the backend.
   */
  static async RefreshSession(callback) {
    if (refreshSessionPromise) return refreshSessionPromise

    if (!callback) callback = window.location.pathname.slice(1) + window.location.search

    // The refresh token rotates on every refresh and cookies are shared by all tabs, so refreshes are
    // serialized across tabs. If it still fails, another tab may have already renewed the session.
    refreshSessionPromise = this.RunWithRefreshLock(async () => (await this.RequestSessionRefresh()) || (await this.HasValidSession()))
      .catch(() => false)
      .then(refreshed => {
        if (!refreshed && !this.PATHS_NOT_NEED_LOGIN.includes(window.location.pathname)) this.NotifySessionExpired(callback)
        return refreshed
      })

    try {
      return await refreshSessionPromise
    } finally {
      refreshSessionPromise = null
    }
  }

  /**
   * Runs the given task holding a lock shared by every tab of this origin (when the Web Locks API is available).
   * @param {function} task Async function to run while holding the lock.
   */
  static RunWithRefreshLock(task) {
    if (typeof navigator !== 'undefined' && navigator.locks && navigator.locks.request) {
      return navigator.locks.request(this.REFRESH_SESSION_LOCK_NAME, task)
    }
    return task()
  }

  /**
   * Sends the session refresh request. Request options are built at call time to use the current cookies.
   * @returns {Promise<boolean>} Whether the session was refreshed.
   */
  static async RequestSessionRefresh() {
    var requestOptions = this.GetRequestOptions('PUT', null)
    delete requestOptions.headers['Content-Type']

    try {
      const response = await fetch(this.devPrefix + this.AUTH_PATH + '/' + this.SESSION_PATH, requestOptions)
      await response.text()
      return response.ok && response.status !== 204
    } catch (error) {
      return false
    }
  }

  /**
   * Checks whether the current session cookies hold a valid session.
   * @returns {Promise<boolean>} Whether a valid session exists.
   */
  static async HasValidSession() {
    try {
      const response = await fetch(this.devPrefix + this.AUTH_PATH + '/' + this.SESSION_PATH, this.GetRequestOptions('GET', null))
      await response.text()
      return response.status === 200
    } catch (error) {
      return false
    }
  }

  /**
   * Modifies the current session so that becomes bound to a given realm.
   * The user must have granted some role to the specified realm.
   * @param {string} realm The realm the session becomes bound to
   * @returns {Session} A newly generated session so that it embeds the bound realm
   */
  static async ChangeSessionRealm(realm) {
    var verb = 'PUT'
    var path = this.AUTH_PATH + '/' + this.REALMS_PATH
    var body = realm

    return fetch(this.devPrefix + path, this.GetRequestOptions(verb, body))
      .then(response => response.status === 204 ? null : response.status === 200 ? response.json() : response.text())
      .then(data => {
        if (data == null || data.toString().startsWith("[ERR")) return data

        return null
      })
  }

  /**
   * Invalidates the current session so that the refresh token no longer works.
   * @returns {bool} Whether the user has been actually logged out, otherwise no session was really open
   */
  static async LogOut() {
    var verb = 'DELETE'
    var path = this.AUTH_PATH + '/' + this.SESSION_PATH
    var body = null

    return fetch(this.devPrefix + path, this.GetRequestOptions(verb, body))
      .then(response => response.text())
      .then(response => response.length ? JSON.parse(response) : null)
      .then(data => data)
  }

  //
  // Permissions management.
  //

  // TODO: more about permissions management. Just implemented the necessary so far.

  /**
   * Provides all those realms the user behind the calling session has some role available for.
   * These are those working groups the calling user is allowed to open session for.
   * This method is to be called by every logged in user in order to know his available working groups.
   * @returns {RealmRoleAssignment[]} Realm identifiers
   */
  static async GetAvailableRealmsForSession() {
    var verb = 'GET'
    var path = this.AUTH_PATH + '/' + this.REALMS_PATH + "/" + this.SESSION_PATH
    var body = null

    return fetch(this.devPrefix + path, this.GetRequestOptions(verb, body))
      .then((response) => response.status === 204 ? null : response.status === 200 ? response.json() : response.text())
  }

  /**
 * Provides the current values of different options applying to a user.
 * This method is to be called by system administrators.*
 * @returns {UserFeatures}
 */
  static async GetUserFeatures(userId) {
    var verb = 'GET'
    var path = this.AUTH_PATH + '/' + this.USER_FEATURES_PATH + "/" + userId
    var body = null

    return this.Fetch(this.devPrefix + path, verb, body)
  }
  /**
   * Changes the options available for a user.
   * This method is to be called by system administrators
   * @param {UserFeatures} features
   * @returns 
   */
  static async StoreUserFeatures(features) {
    var verb = 'POST'
    var path = this.AUTH_PATH + '/' + this.USER_FEATURES_PATH
    var body = features
    
    return this.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Checking looking for users whose passwords are close to expire.
   * @param {WakeupEvent} eventInfo Callback information from the sched service: appointmentId, payload, next
   * @returns The amount of warning e-mails actually sent in this call
   */
  static async PasswordExpirationCheck(eventInfo) {
    var verb = 'POST'
    var path = this.AUTH_PATH + '/' + this.PASSWORD_EXPIRATION_CHECK_PATH
    var body = eventInfo

    return this.Fetch(this.devPrefix + path, verb, body)

  }

  /**
   * Change the password of a user.
   * @param {RepalceCredentials} credentials {string userEmail, string currentPassword, string newPassword}
   * @returns Error message if the change did not succed. Null on success
   */
  static async ReplacePassword(credentials) {
    var verb = 'PUT'
    var path = this.AUTH_PATH + '/' + this.PASSWORDS_PATH
    var body = credentials

    return this.Fetch(this.devPrefix + path, verb, body)
  }

  /**
  *  Provides the time-stamp when the password of the logged in user is set to expire
  */
  static async GetPasswordExpiration() {
    var verb = 'GET'
    var path = this.AUTH_PATH + '/' + this.PASSWORDS_PATH
    var body = null

    return this.Fetch(this.devPrefix + path, verb, body)
  }

  /**
  * Provides information about the users the calling user is allowed to manage.
  * This is for billable administrators, all users granted to work on the caller's billable are provided.
  * This is a paging request, the response may not contain all users.
  * For paging considerations, the collection of users of the calling user is provided sorted by sign up time descending.
  * @returns UserInformation[]: {string userId, string userEmail, DateTime? passwordExpiration, DateTime? lockoutEnd, int accessFailedCount}
  */
  static async GetBillableUsers(pageSize, page) {
    var verb = 'GET'
    var path = this.AUTH_PATH + '/' + this.BILLABLE_USERS_PATH + '?' + this.PAGE_SIZE_QUERY + '=' + pageSize + '&' + this.PAGE_QUERY + '=' + page
    var body = null
  
    return this.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides information about all users in the system.
   * This is for system administrators.
   * This is a paging request, the response may not contain all users.
   * For paging considerations, the collection of users of the calling user is provided sorted by sign up time descending.
   * @returns UserInformation[]: {string userId, string userEmail, DateTime? passwordExpiration, DateTime? lockoutEnd, int accessFailedCount}
   */
  static async GetAllUsers(pageSize, page) {
    var verb = 'GET'
    var path = this.AUTH_PATH + '/' + this.ALL_USERS_PATH + '?' + this.PAGE_SIZE_QUERY + '=' + pageSize + '&' + this.PAGE_QUERY + '=' + page
    var body = null
  
    return this.Fetch(this.devPrefix + path, verb, body)
  }

  //
  // Permissions management.
  //

  /**
   * Establishes a role to be granted to a user within a realm.
   * From now on all permission linked to that role will be considered granted to the user
   * except those explicitly denied (personalized denied permissions).
   * In case the user already had another role granted it will be replaced.
   * @param {string} userId Internal identifier of the user to have the role granted
   * @param {string} realm Realm the role will be granted within
   * @param {string} role Identifier of the role being granted
   * @returns 
   */
  static async GrantRoleToUser(userId, realm, role) {
    var verb = 'PUT'
    var path = this.AUTH_PATH + '/' + this.ROLES_PATH + '?' + this.USER_ID_QUERY + '=' + userId + '&' + this.REALM_QUERY + '=' + encodeURIComponent(realm)
    var body = role

    return this.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Removes a previously established role to be denied to a user within a realm.
   * From now on all permission linked to that role will be considered NOT granted to the user
   * except those explicitly granted (personalized granted permissions).
   * @param {string} userId Internal identifier of the user to have the role denied
   * @param {string} realm Realm the role will be denied within
   * @param {string} role Identifier of the role being denied
   * @returns 
   */
  static async DenyRoleToUser(userId, realm, role) {
    var verb = 'DELETE'
    var path = this.AUTH_PATH + '/' + this.ROLES_PATH + '?' + this.USER_ID_QUERY + '=' + userId + '&' + this.REALM_QUERY + '=' + encodeURIComponent(realm) + '&' + this.ROLE_QUERY + '=' + role
    var body = null

    return this.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Modifies the personalized permissions of a user within a realm so that
   * a new permission is added to the list of permissions granted 
   * beyond those implicit by the role.
   * @param {string} userId Internal identifier of the user to have the permission granted
   * @param {string} realm Realm within which the user will have the permission granted
   * @param {string} permissionCode Identifier of the permission to be granted
   * @returns 
   */
  static async AddGrantPermissionToUser(userId, realm, permissionCode) {
    var verb = 'PUT'
    var path = this.AUTH_PATH + '/' + this.PERMISSIONS_PATH + '/' + this.GRANTED_PATH + '?' + this.USER_ID_QUERY + '=' + userId + '&' + this.REALM_QUERY + '=' + encodeURIComponent(realm)
    var body = permissionCode

    return this.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Modifies the personalized permissions of a user within a realm so that
   * an existing personalized permission is removed from the list of permissions granted 
   * beyond those implicit by the role.
   * @param {string} userId Internal identifier of the user to have the permission removed
   * @param {string} realm Realm within which the user will have the permission removed
   * @param {string} permissionCode Identifier of the permission to be removed
   * @returns 
   */
  static async DeleteGrantPermissionToUser(userId, realm, permissionCode) {
    var verb = 'DELETE'
    var path = this.AUTH_PATH + '/' + this.PERMISSIONS_PATH + '/' + this.GRANTED_PATH + '?' + this.USER_ID_QUERY + '=' + userId + '&' + this.REALM_QUERY + '=' + encodeURIComponent(realm) + '&' + this.PERMISSION_CODE_QUERY + '=' + permissionCode
    var body = null

    return this.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Modifies the personalized permissions of a user within a realm so that
   * a new permission is added to the list of permissions denied 
   * beyond those implicit by the role.
   * @param {string} userId Internal identifier of the user to have the permission denied
   * @param {string} realm Realm within which the user will have the permission denied
   * @param {string} permissionCode Identifier of the permission to be denied
   * @returns 
   */
  static async AddDenyPermissionToUser(userId, realm, permissionCode) {
    var verb = 'PUT'
    var path = this.AUTH_PATH + '/' + this.PERMISSIONS_PATH + '/' + this.DENIED_PATH + '?' + this.USER_ID_QUERY + '=' + userId + '&' + this.REALM_QUERY + '=' + encodeURIComponent(realm)
    var body = permissionCode

    return this.Fetch(this.devPrefix + path, verb, body)
  }

  /**
  * Modifies the personalized permissions of a user within a realm so that
  * an existing personalized permission is removed from the list of permissions denied 
  * beyond those implicit by the role.
  * @param {string} userId Internal identifier of the user to have the permission restored
  * @param {string} realm Realm within which the user will have the permission restored
  * @param {string} permissionCode Identifier of the permission to be restored
  * @returns 
  */
  static async DeleteDenyPermissionToUser(userId, realm, permissionCode) {
    var verb = 'DELETE'
    var path = this.AUTH_PATH + '/' + this.PERMISSIONS_PATH + '/' + this.DENIED_PATH + '?' + this.USER_ID_QUERY + '=' + userId + '&' + this.REALM_QUERY + '=' + encodeURIComponent(realm) + '&' + this.PERMISSION_CODE_QUERY + '=' + permissionCode
    var body = null

    return this.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides all codes of all permissions declared in the system.
   * The description provided will include the description in the requested
   * language only.
   * @param {*} culture ISO code of the desired language for the permissions' descriptions
   * @returns 
   */
  static async GetAllPermissions(culture) {
    var verb = 'GET'
    var path = this.AUTH_PATH + '/' + this.PERMISSIONS_PATH + '?' + this.CULTURE_QUERY + '=' + culture
    var body = null

    return this.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides all those permission codes specifically granted to the user within a realm.
   * These are those custom permissions granted additionally to those implicit by the assigned role.
   * Any other permission also granted implicitly due to the assigned role is not included in the provided content.
   * @param {string} userId Internal identifier of the user asking for permissions added in a realm
   * @param {string} realm Realm asking for permissions added to a user
   * @returns 
   */
  static async GetPermissionsGranted(userId, realm) {
    var verb = 'GET'
    var path = this.AUTH_PATH + '/' + this.PERMISSIONS_PATH + '/' + this.GRANTED_PATH + '?' + this.USER_ID_QUERY + '=' + userId + '&' + this.REALM_QUERY + '=' + encodeURIComponent(realm)
    var body = null

    return this.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides all those permission codes specifically denied to the user within a realm.
   * These are those custom permissions denied subtracted from those implicit by the role assigned.
   * Any other permissions not granted implicitly due to not assigned to a role are not included in the provided content.
   * @param {string} userId Internal identifier of the user asking for permissions denied in a realm
   * @param {string} realm Realm asking for permissions denied to a user
   * @returns 
   */
  static async GetPermissionsDenied(userId, realm) {
    var verb = 'GET'
    var path = this.AUTH_PATH + '/' + this.PERMISSIONS_PATH + '/' + this.DENIED_PATH + '?' + this.USER_ID_QUERY + '=' + userId + '&' + this.REALM_QUERY + '=' + encodeURIComponent(realm)
    var body = null

    return this.Fetch(this.devPrefix + path, verb, body)
  }
  
  /**
   * Unregisters a user from the system.
   * This user will never be able to log in.
   * To be called by system administrators.  
   * @param {string} userId Internal identifier of the user to be removed</param>
   * @returns {bool}Error message in case of invalid user identifier. Null on success
   */
  static async DropOutUser(userId) {
    var verb = 'DELETE'
    var path = this.AUTH_PATH + '/' + this.USER_PATH + '/' + userId
    var body = null

    return this.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides all those realms a given user has some role available for.
   * These are those working groups the specified user is allowed to open session for.
   *This method is to be called by system administrators to manage permissions.
   * @param {string} userId Internal unique identifier of the 
   * @returns RealmRoleAssignment[] An entry per assignment indicating the role the user has granted within a realm
   */
  static async GetAvailableRealmsForUser(userId) {
    var verb = 'GET'
    var path = this.AUTH_PATH + '/' + this.REALMS_PATH + '/' + this.USER_PATH + '?' + this.USER_ID_QUERY + '=' + userId
    var body = null

    return this.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Builds the request options for each API request.
   * Includes credential forwarding and CSRF for state-changing verbs.
   * @param {bool} multipart Whether this fetch is for the particular case of a multi-part request
   */
  static GetRequestOptions(verb, body, multipart) {
    var culture = window.localStorage.getItem('culture')
    var csrfToken = this.GetCookie(this.CSRF_COOKIE_NAME)
    var includeCsrf = this.IsStateChangingVerb(verb) && csrfToken

    if (culture === null || culture === '') culture = 'en'

    // All requests except multi-part include json content.
    if (multipart) {
      var multipartHeaders = {
        'culture': culture,
        [this.AUTH_MODE_HEADER_NAME]: this.AUTH_MODE_COOKIE_VALUE
      }
      if (includeCsrf) multipartHeaders[this.CSRF_HEADER_NAME] = csrfToken

      return {
        method: verb,
        credentials: 'include',
        headers: multipartHeaders,
        body: body
      }
    } else if (body == null) {
      var headersWithoutBody = {
        'Content-Type': 'application/json',
        'culture': culture,
        [this.AUTH_MODE_HEADER_NAME]: this.AUTH_MODE_COOKIE_VALUE
      }
      if (includeCsrf) headersWithoutBody[this.CSRF_HEADER_NAME] = csrfToken

      return {
        method: verb,
        credentials: 'include',
        headers: headersWithoutBody
      }
    } else {
      var headersWithBody = {
        'Content-Type': 'application/json',
        'culture': culture,
        [this.AUTH_MODE_HEADER_NAME]: this.AUTH_MODE_COOKIE_VALUE
      }
      if (includeCsrf) headersWithBody[this.CSRF_HEADER_NAME] = csrfToken

      return {
        method: verb,
        credentials: 'include',
        headers: headersWithBody,
        body: JSON.stringify(body)
      }
    }
  }

  /**
   * To be used by other service clients in order to send API calls
   * including the authentication header. Also, calls are automatically
   * retried when the authentication header expires.
   * @param {bool} multipart Whether this fetch is for the particular case of a multi-part request
   */
  static async Fetch(path, verb, body, multipart) {
    // Getting relative url path for callback in case api calls fail
    let callback = window.location.pathname.slice(1) + window.location.search

    return fetch(path, this.GetRequestOptions(verb, body, multipart))
      .then(async (response) => {
        if (response.status === 401) {
          console.log("Refreshing session...")
          const refreshed = await this.RefreshSession(callback)
          if (!refreshed) return this.SESSION_EXPIRED_RESPONSE
          return fetch(path, this.GetRequestOptions(verb, body, multipart)).then((response) => {
            if (response.status === 401) {
              this.NotifySessionExpired(callback)
              return this.SESSION_EXPIRED_RESPONSE
            }
            else return response
          })
        } else {
          return response
        }
      })
      .then((response) => {
        if (response == null) return null
        if (typeof response === 'string') return response
        if (response.status === 204) return null
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) return response.json()
        return response.text()
      })
  }
  
  /**
    * A user has forgotten the password and requests its recovery.
    * The service sends an e-mail to the specified user with a link to set a new password.
    * @param {string} userEmail Internal unique identifier of the 
    * @returns Error message if the user does not exist or a request is already in progress. Null on success</
    */
  static async RequestPasswordRecovery(userEmail) {
    var verb = 'POST'
    var path = this.AUTH_PATH + '/' + this.PASSWORD_RECOVERY_PATH
    var body = userEmail

    return this.Fetch(this.devPrefix + path, verb, body)
  }
  
  /**
     * Changes the password of a user that forgot the old password.
     * @param {string} credentials Note that credentials.currentPassword here contains the token received by e-mail (instead of the old password)
     * @returns  Error message if the change did not succeed. Null on success
     */
  static async RecoverPassword(credentials) {
    var verb = 'PUT'
    var path = this.AUTH_PATH + '/' + this.PASSWORD_RECOVERY_PATH
    var body = credentials

    return this.Fetch(this.devPrefix + path, verb, body)
  }

}


// WEBPACK FOOTER //
// ./src/sdk/AuthCli.js